import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { components } from '@awj/api-client';

import { useI18n } from '~/i18n/index.tsx';

import { fetchAllPages } from './export.ts';
import { admin } from './queries.ts';

type Transition = components['schemas']['TransitionOrderRequest'];

export interface OrderQuery {
  search?: string;
  status?: string;
  from?: string;
  to?: string;
  refundDue?: boolean;
  page?: number;
  pageSize?: number;
}

/** The filters as the API takes them — shared by the list and its export, so the two agree. */
function filters(query: OrderQuery) {
  return {
    search: query.search || undefined,
    status: query.status || undefined,
    from: query.from || undefined,
    to: query.to || undefined,
    refundDue: query.refundDue ? 'true' : undefined,
  };
}

export function useOrders(query: OrderQuery) {
  return useQuery({
    queryKey: ['orders', 'list', query],
    queryFn: () =>
      admin().orders({ ...filters(query), page: query.page ?? 1, pageSize: query.pageSize ?? 20 }),
  });
}

/** Every order the filters match, for an export. */
export function allOrders(query: OrderQuery) {
  return fetchAllPages((page, pageSize) => admin().orders({ ...filters(query), page, pageSize }));
}

/** Read in the panel's language: an order placed in Arabic shows English line names to English staff. */
export function useOrder(number: string) {
  const { locale } = useI18n();

  return useQuery({
    queryKey: ['orders', 'one', number, locale],
    queryFn: () => admin().order(number, locale),
  });
}

export function useOrderTransitions(number: string) {
  return useQuery({
    queryKey: ['orders', 'transitions', number],
    queryFn: () => admin().orderTransitions(number),
  });
}

/** Records that a cancelled order's payment went back to the customer. */
export function useMarkRefunded(number: string) {
  const queryClient = useQueryClient();
  const { locale } = useI18n();

  return useMutation({
    mutationFn: (reference: string) =>
      admin().markOrderRefunded(number, { reference: reference.trim() || null }, locale),
    onSuccess: (order) => {
      queryClient.setQueryData(['orders', 'one', number, locale], order);
      void queryClient.invalidateQueries({ queryKey: ['orders', 'list'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

/**
 * Moving an order along.
 *
 * Everything the change touches is dropped: the order itself, what it may do next, the list it
 * appears in, and the dashboard — shipping an order empties the "to prepare" tile, and a stale
 * counter is how staff pick the same order twice.
 */
export function useTransitionOrder(number: string) {
  const queryClient = useQueryClient();
  const { locale } = useI18n();

  return useMutation({
    mutationFn: (body: Transition) => admin().transitionOrder(number, body, locale),
    onSuccess: (order) => {
      queryClient.setQueryData(['orders', 'one', number, locale], order);
      void queryClient.invalidateQueries({ queryKey: ['orders', 'transitions', number] });
      void queryClient.invalidateQueries({ queryKey: ['orders', 'list'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}
